/**
 * API 相关类型定义
 */

import { Request, Response, NextFunction } from 'express';
import type { UnifiedTask } from '../adapters/base-adapter.js';

// API 服务器选项
export interface ApiServerOptions {
  port?: number;
  basePath?: string;
  version?: string;
  enableSSE?: boolean;
  enableAuth?: boolean;
  enableGraphQL?: boolean;
}

// 扩展 Express Request
export interface ApiRequest extends Request {
  apiVersion?: string;
  userId?: string;
  taskService?: any; // 将来可以注入服务
}

// 统一的 API 响应格式
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  meta?: {
    timestamp: string;
    version: string;
    requestId?: string;
    project?: {
      id: string;
      name: string;
      description?: string;
    } | null;
  };
}

// 分页参数
export interface PaginationParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

// 分页响应
export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// SSE 事件类型
export enum SSEEventType {
  TASK_CREATED = 'task.created',
  TASK_UPDATED = 'task.updated',
  TASK_DELETED = 'task.deleted',
  TASK_STARTED = 'task.started',
  TASK_COMPLETED = 'task.completed',
  WORKFLOW_STARTED = 'workflow.started',
  WORKFLOW_COMPLETED = 'workflow.completed',
  WORKFLOW_FAILED = 'workflow.failed',
  SYSTEM_NOTIFICATION = 'system.notification',
  HEARTBEAT = 'heartbeat',
}

// SSE 事件数据
export interface SSEEvent {
  type: SSEEventType;
  data: any;
  timestamp: string;
  id?: string;
}

// 任务查询参数
export interface TaskQueryParams extends PaginationParams {
  status?: string;
  priority?: string;
  assignee?: string;
  tags?: string | string[];
  search?: string;
  startDate?: string;
  endDate?: string;
}

// 工作流查询参数
export interface WorkflowQueryParams extends PaginationParams {
  status?: string;
  type?: string;
  search?: string;
}

// 错误代码
export enum ApiErrorCode {
  // 通用错误
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  NOT_FOUND = 'NOT_FOUND',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  RATE_LIMITED = 'RATE_LIMITED',
  
  // 任务相关错误
  TASK_NOT_FOUND = 'TASK_NOT_FOUND',
  TASK_ALREADY_EXISTS = 'TASK_ALREADY_EXISTS',
  TASK_INVALID_STATUS = 'TASK_INVALID_STATUS',
  TASK_DEPENDENCY_ERROR = 'TASK_DEPENDENCY_ERROR',
  
  // 工作流相关错误
  WORKFLOW_NOT_FOUND = 'WORKFLOW_NOT_FOUND',
  WORKFLOW_ALREADY_RUNNING = 'WORKFLOW_ALREADY_RUNNING',
  WORKFLOW_INVALID_CONFIG = 'WORKFLOW_INVALID_CONFIG',
}

// API 错误类
export class ApiError extends Error {
  constructor(
    public code: ApiErrorCode,
    public message: string,
    public statusCode: number = 400,
    public details?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}